import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { DynamicIcon } from '@/utils/iconMap';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AboutPage = () => {
  const { settings } = useSiteSettings();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/about-content`);
        setContent(res.data);
      } catch (error) {
        console.error('Failed to fetch about content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const whatsapp = settings?.whatsapp || '85295858258';
  const heroTitle = content?.hero_title || '關於智駕駛';
  const heroSubtitle = content?.hero_subtitle || '用心教學，助你輕鬆考取駕駛執照';
  const intro1 = content?.intro_1 || '';
  const intro2 = content?.intro_2 || '';
  const features = content?.features || [];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 tracking-tight">
            {heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">載入中...</div>
          ) : (
            <>
              {/* Main Content Card */}
              <Card className="overflow-hidden border-0 shadow-lg bg-white mb-10">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600" />
                <CardContent className="p-8 md:p-12">
                  <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed">
                    {intro1 && (
                      <p className="text-lg whitespace-pre-line">{intro1}</p>
                    )}
                    {intro2 && (
                      <p className="text-lg whitespace-pre-line">{intro2}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Features Grid */}
              {features.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  {features.map((feature) => (
                    <Card key={feature.id} className="border-0 shadow-md hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className="flex gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                              <DynamicIcon name={feature.icon} className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div>
                            <h3 className="font-bold text-blue-600 text-lg mb-2">{feature.title}</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* CTA */}
          <div className="text-center">
            <Button asChild size="lg" className="rounded-full bg-green-500 hover:bg-green-600 text-white px-8">
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp 查詢
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
